import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
} from '@mui/material';
import {
  ArrowDropDown as ArrowDropDownIcon,
  FileDownload as CsvIcon,
  Computer,
  CheckCircle,
  Cancel,
  AccessTime,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import moment from 'moment-timezone';

interface DeviceUptimeRecord {
  id: string;
  deviceId: string;
  deviceName: string | null;
  userName: string | null;
  email: string | null;
  agentVersion: string | null;
  lastHeartbeat: string | null;
  isTracked: boolean;
  isActive: boolean;
  status: 'online' | 'recent' | 'offline';
  hoursSinceLastHeartbeat: number;
}

const DeviceUptimeReportPage: React.FC = () => {
  const { user } = useAuth();
  const { trackingEnabled } = useSettings();
  const [filters, setFilters] = useState({
    deviceId: '',
  });
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortBy, setSortBy] = useState<'deviceName' | 'userName' | 'lastHeartbeat' | 'status'>('lastHeartbeat');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Check if user has permission
  const isAdministratorOrAbove = user?.role && ['administrator', 'super_user'].includes(user.role);

  // Fetch devices for filter
  const { data: devicesData } = useQuery({
    queryKey: ['devices'],
    queryFn: () => apiClient.devices.getAll({ isActive: true }),
    enabled: isAdministratorOrAbove && trackingEnabled,
  });

  // Fetch device uptime data
  const { data: uptimeData, isLoading, error, refetch } = useQuery({
    queryKey: ['deviceUptime', filters],
    queryFn: async () => {
      const response = await apiClient.activityTracking.getDeviceUptime({
        startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        deviceId: filters.deviceId || undefined,
      });
      return response.data;
    },
    enabled: isAdministratorOrAbove && trackingEnabled,
    refetchInterval: 60000, // Refresh every minute
  });

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({ deviceId: '' });
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportCSV = () => {
    if (!uptimeData?.data) return;

    const csvRows: string[] = [];
    csvRows.push('Device ID,Device Name,User,Email,Agent Version,Last Heartbeat,Status,Hours Since Last Heartbeat,Is Tracked,Is Active');

    uptimeData.data.forEach((record: DeviceUptimeRecord) => {
      csvRows.push([
        record.deviceId,
        record.deviceName || 'Unnamed',
        record.userName || 'Unassigned',
        record.email || 'N/A',
        record.agentVersion || 'Unknown',
        record.lastHeartbeat ? moment(record.lastHeartbeat).format('YYYY-MM-DD HH:mm:ss') : 'Never',
        record.status,
        (record.hoursSinceLastHeartbeat || 0).toFixed(1),
        record.isTracked ? 'Yes' : 'No',
        record.isActive ? 'Yes' : 'No',
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `device-uptime-${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    handleExportMenuClose();
  };

  const toggleSort = (col: 'deviceName' | 'userName' | 'lastHeartbeat' | 'status') => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir(col === 'lastHeartbeat' ? 'desc' : 'asc');
    }
  };

  const sortedData = React.useMemo(() => {
    if (!uptimeData?.data) return [];
    const copy = [...uptimeData.data];
    copy.sort((a, b) => {
      let av: any = '';
      let bv: any = '';
      switch (sortBy) {
        case 'deviceName':
          av = (a.deviceName || a.deviceId).toLowerCase();
          bv = (b.deviceName || b.deviceId).toLowerCase();
          break;
        case 'userName':
          av = (a.userName || '').toLowerCase();
          bv = (b.userName || '').toLowerCase();
          break;
        case 'lastHeartbeat':
          av = a.lastHeartbeat ? new Date(a.lastHeartbeat).getTime() : 0;
          bv = b.lastHeartbeat ? new Date(b.lastHeartbeat).getTime() : 0;
          break;
        case 'status':
          const statusOrder = { online: 0, recent: 1, offline: 2 };
          av = statusOrder[a.status as keyof typeof statusOrder];
          bv = statusOrder[b.status as keyof typeof statusOrder];
          break;
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [uptimeData?.data, sortBy, sortDir]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'success';
      case 'recent': return 'warning';
      case 'offline': return 'error';
      default: return 'default';
    }
  };

  const formatLastHeartbeat = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return moment(timestamp).format('MMM D, YYYY HH:mm');
  };

  if (!isAdministratorOrAbove) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Device Uptime Report
        </Typography>
        <Alert severity="warning" sx={{ mt: 3 }}>
          Access denied. Only administrators and above can access this report.
        </Alert>
      </Container>
    );
  }

  if (!trackingEnabled) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Device Uptime Report
        </Typography>
        <Alert severity="info" sx={{ mt: 3 }}>
          Tracking is currently disabled. Enable tracking in Settings to view device reports.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Device Uptime Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor device connectivity and agent status
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowDropDownIcon />}
          onClick={handleExportMenuOpen}
          disabled={!uptimeData?.data || uptimeData.data.length === 0}
        >
          Export
        </Button>
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={handleExportCSV}>
            <ListItemIcon>
              <CsvIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export as CSV</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={devicesData?.data?.data || []}
                getOptionLabel={(option: any) => `${option.deviceName || option.deviceId} (${option.deviceId})`}
                value={devicesData?.data?.data?.find((d: any) => d.deviceId === filters.deviceId) || null}
                onChange={(_, newValue: any) => handleFilterChange('deviceId', newValue?.deviceId || '')}
                renderInput={(params) => <TextField {...params} label="Filter by Device" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', height: '100%' }}>
                <Button variant="contained" onClick={() => refetch()}>
                  Refresh
                </Button>
                <Button variant="outlined" onClick={handleResetFilters}>
                  Reset Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load device uptime data. Please try again.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Device Status
          </Typography>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : sortedData.length === 0 ? (
            <Alert severity="info">No devices found.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'deviceName'}
                        direction={sortBy === 'deviceName' ? sortDir : 'asc'}
                        onClick={() => toggleSort('deviceName')}
                      >
                        Device
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'userName'}
                        direction={sortBy === 'userName' ? sortDir : 'asc'}
                        onClick={() => toggleSort('userName')}
                      >
                        User
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Agent Version</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === 'lastHeartbeat'}
                        direction={sortBy === 'lastHeartbeat' ? sortDir : 'asc'}
                        onClick={() => toggleSort('lastHeartbeat')}
                      >
                        Last Heartbeat
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">
                      <TableSortLabel
                        active={sortBy === 'status'}
                        direction={sortBy === 'status' ? sortDir : 'asc'}
                        onClick={() => toggleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">Tracking</TableCell>
                    <TableCell align="center">Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((record) => (
                    <TableRow key={record.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.875rem">
                            {record.deviceId}
                          </Typography>
                          {record.deviceName && (
                            <Typography variant="caption" color="text.secondary">
                              {record.deviceName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {record.userName ? (
                          <Box>
                            <Typography variant="body2">{record.userName}</Typography>
                            {record.email && (
                              <Typography variant="caption" color="text.secondary">
                                {record.email}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Unassigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {record.agentVersion || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatLastHeartbeat(record.lastHeartbeat)}
                          </Typography>
                          {record.lastHeartbeat && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              ({(record.hoursSinceLastHeartbeat || 0).toFixed(1)}h ago)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={record.status === 'online' ? <CheckCircle /> : <Cancel />}
                          label={record.status.toUpperCase()}
                          size="small"
                          color={getStatusColor(record.status) as any}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={record.isTracked ? 'Tracked' : 'Excluded'}
                          size="small"
                          color={record.isTracked ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={record.isActive ? 'Active' : 'Inactive'}
                          size="small"
                          color={record.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default DeviceUptimeReportPage;

